import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { CopyIcon, TriangleAlertIcon, WarehouseIcon } from '@/shared/ui/icons';
import { useToast } from '@/shared/hooks/useToast';

const FULL_NAME = 'Abdusalamov Jaxongir';
const ADDRESS_LINE_1 = '109 ROGERS RD STE 3';
const ADDRESS_LINE_2 = 'WS027971';
const CITY = 'Wilmington';
const STATE = 'Delaware';
const ZIPCODE = '19801';
const TELEPHONE = '+1 (302) 396-6563';

type Field = {
  key: string;
  label: string;
  value: string;
};

export const WidgetsOnlineAddressMain = () => {
  const { t } = useTranslation('onlineAddress');
  const toast = useToast();

  const copy = async (value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('copied'));
    } catch {
      toast.error(t('copyFailed'));
    }
  };

  const fields: Field[] = [
    { key: 'fullName', label: t('fields.fullName'), value: FULL_NAME },
    { key: 'addressLine1', label: t('fields.addressLine1'), value: ADDRESS_LINE_1 },
    { key: 'addressLine2', label: t('fields.addressLine2'), value: ADDRESS_LINE_2 },
    { key: 'city', label: t('fields.city'), value: CITY },
    { key: 'state', label: t('fields.state'), value: STATE },
    { key: 'zipcode', label: t('fields.zipcode'), value: ZIPCODE },
    { key: 'telephone', label: t('fields.telephone'), value: TELEPHONE },
  ];

  return (
    <>
      <PageHeader title={t('title')} showBack backTo="/settings" />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
          <div className="flex items-center gap-3 border-b border-slate-100 bg-brand-700 px-4 py-3 text-white">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-400 text-brand-900">
              <WarehouseIcon className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-brand-100">DELAWARE</p>
              <p className="truncate text-sm text-brand-50">{t('intro')}</p>
            </div>
          </div>

          <ul>
            {fields.map((field, index) => (
              <li key={field.key} className={index > 0 ? 'border-t border-slate-100' : undefined}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-ink-muted">{field.label}</p>
                    <p className="break-words text-sm font-semibold text-ink">{field.value}</p>
                  </div>
                  <IconButton
                    label={t('copy')}
                    variant="ghost"
                    size="sm"
                    onClick={() => void copy(field.value)}
                  >
                    <CopyIcon className="h-5 w-5 text-brand-600" />
                  </IconButton>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
          <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
          <p className="text-sm font-medium text-danger">{t('warning')}</p>
        </div>
      </div>
    </>
  );
};
